/**
 * LingeringFart - Area denial fart that spreads, lingers, and slowly dissipates
 * 
 * Behavior:
 * 1. SPREAD: Expands from small to full size over ~1s
 * 2. LINGER: Stays at full size, pulsing gently, dealing tick damage
 * 3. DISSIPATE: Slowly fades and shrinks over ~2s
 */

import Phaser from 'phaser';
import { FartType, FART_CONFIGS, LINGERING_CONFIG } from './FartTypes';

export interface LingeringFartConfig {
    ownerId: string;
    duration?: number;          // Total lifespan in ms (default: 5000)
    damagePerTick?: number;     // Damage per tick (default: 8)
    tickInterval?: number;      // Ms between damage ticks (default: 500)
}

enum LingeringPhase {
    SPREADING,
    LINGERING,
    DISSIPATING,
}

export class LingeringFart extends Phaser.Physics.Arcade.Sprite {
    public readonly fartType = FartType.LINGERING;

    private ownerId: string;
    private damagePerTick: number;
    private tickInterval: number;
    private duration: number;

    private phase: LingeringPhase = LingeringPhase.SPREADING;
    private phaseTimer: number = 0;
    private tickTimer: number = 0;
    private hitPlayers: Set<string> = new Set();     // Track who we've hit this tick

    // Phase durations
    private readonly SPREAD_DURATION = 800;          // Time to reach full size
    private readonly DISSIPATE_DURATION = 2000;      // Time to fade out

    // Scale ranges
    private readonly MIN_SCALE = 0.3;
    private readonly MAX_SCALE = 1.8;

    // Pulse effect
    private pulseDirection: number = 1;
    private pulseAmount: number = 0;

    constructor(scene: Phaser.Scene, x: number, y: number, config: LingeringFartConfig) {
        super(scene, x, y, 'fart_lingering');

        this.ownerId = config.ownerId;
        this.duration = config.duration ?? FART_CONFIGS[FartType.LINGERING].duration;
        this.damagePerTick = config.damagePerTick ?? FART_CONFIGS[FartType.LINGERING].damage;
        this.tickInterval = config.tickInterval ?? LINGERING_CONFIG.tickInterval;

        scene.add.existing(this);
        scene.physics.add.existing(this);

        // Start small
        this.setScale(this.MIN_SCALE);
        this.setAlpha(0.7);
        this.setDepth(35); // Below players but above floor

        // Set up physics body - circular
        const body = this.body as Phaser.Physics.Arcade.Body;
        body.setCircle(24); // Base radius, will be scaled
        body.setOffset(0, 0);
        body.setImmovable(true);

        // Store data for damage system
        this.setData('ownerId', this.ownerId);
        this.setData('fartType', FartType.LINGERING);
        this.setData('isLingering', true);
    }

    /**
     * Get owner ID (for self-damage checks)
     */
    getOwnerId(): string {
        return this.ownerId;
    }

    /**
     * Get damage amount
     */
    getDamage(): number {
        return this.damagePerTick;
    }

    /**
     * Get stun duration
     */
    getStunDuration(): number {
        return FART_CONFIGS[FartType.LINGERING].stunDuration;
    }

    /**
     * Check if a player can be damaged this tick (prevents multi-hit per tick)
     */
    canDamagePlayer(playerId: string): boolean {
        // Owner is immune to their own lingering fart
        if (playerId === this.ownerId) return false;

        // Already hit this tick?
        if (this.hitPlayers.has(playerId)) return false;

        return true;
    }

    /**
     * Mark a player as hit this tick
     */
    markPlayerHit(playerId: string): void {
        this.hitPlayers.add(playerId);
    }

    /**
     * Update the lingering fart each frame
     */
    update(time: number, delta: number): void {
        if (!this.active) return;

        this.phaseTimer += delta;
        this.tickTimer += delta;

        // Reset hit tracking each tick
        if (this.tickTimer >= this.tickInterval) {
            this.tickTimer = 0;
            this.hitPlayers.clear();
        }

        switch (this.phase) {
            case LingeringPhase.SPREADING:
                this.updateSpreadingPhase(delta);
                break;
            case LingeringPhase.LINGERING:
                this.updateLingeringPhase(delta);
                break;
            case LingeringPhase.DISSIPATING:
                this.updateDissipatingPhase(delta);
                break;
        }

        // Update physics body to match visual scale
        this.updatePhysicsBody();
    }

    private updateSpreadingPhase(delta: number): void {
        const progress = Math.min(1, this.phaseTimer / this.SPREAD_DURATION);

        // Ease out for natural spreading feel
        const easedProgress = 1 - Math.pow(1 - progress, 3);

        // Scale from MIN to MAX
        const scale = this.MIN_SCALE + (this.MAX_SCALE - this.MIN_SCALE) * easedProgress;
        this.setScale(scale);

        // Slight alpha increase as it spreads
        this.setAlpha(0.5 + 0.3 * easedProgress);

        // Transition to lingering phase
        if (progress >= 1) {
            this.phase = LingeringPhase.LINGERING;
            this.phaseTimer = 0;
        }
    }

    private updateLingeringPhase(delta: number): void {
        const lingerDuration = this.duration - this.SPREAD_DURATION - this.DISSIPATE_DURATION;

        // Pulsing effect - gentle breathing
        this.pulseAmount += delta * 0.003 * this.pulseDirection;
        if (this.pulseAmount > 1) {
            this.pulseAmount = 1;
            this.pulseDirection = -1;
        } else if (this.pulseAmount < 0) {
            this.pulseAmount = 0;
            this.pulseDirection = 1;
        }

        const pulseScale = LINGERING_CONFIG.pulseMin +
            (LINGERING_CONFIG.pulseMax - LINGERING_CONFIG.pulseMin) * this.pulseAmount;
        this.setScale(this.MAX_SCALE * pulseScale);

        // Slight rotation for organic feel
        this.rotation += delta * 0.0003;

        // Transition to dissipating phase
        if (this.phaseTimer >= lingerDuration) {
            this.phase = LingeringPhase.DISSIPATING;
            this.phaseTimer = 0;
        }
    }

    private updateDissipatingPhase(delta: number): void {
        const progress = Math.min(1, this.phaseTimer / this.DISSIPATE_DURATION);

        // Ease in for gradual start, faster at end
        const easedProgress = progress * progress;

        // Shrink and fade
        const scale = this.MAX_SCALE * (1 - easedProgress * 0.6); // Shrink to 40% of max
        this.setScale(scale);

        // Fade out
        this.setAlpha(0.8 * (1 - easedProgress));

        // Destroy when fully dissipated
        if (progress >= 1) {
            this.destroy();
        }
    }

    private updatePhysicsBody(): void {
        const body = this.body as Phaser.Physics.Arcade.Body;
        if (!body) return;

        // Scale the hitbox with the visual
        const radius = 20 * this.scaleX;
        const offset = (this.width / 2) - radius;
        body.setCircle(radius, offset, offset);
    }

    /**
     * Check if fart is in a phase where it can deal damage
     */
    canDealDamage(): boolean {
        // Can damage during spreading and lingering, reduced during dissipating
        return this.phase !== LingeringPhase.DISSIPATING || this.phaseTimer < this.DISSIPATE_DURATION * 0.5;
    }

    /**
     * Get current phase for debugging
     */
    getPhase(): string {
        return LingeringPhase[this.phase];
    }
}
